import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Brain, Sparkles } from 'lucide-react';
import { getAgents } from '../services/api';
import '../styles/Placeholder.css';

// Generate avatar gradient based on name
const getAvatarGradient = (name) => {
  const gradients = [
    ['#00ffff', '#00ff88'], // Cyan to Green
    ['#ff00ff', '#00ffff'], // Magenta to Cyan  
    ['#ffaa00', '#ff00aa'], // Orange to Pink
    ['#00ff88', '#00ffff'], // Green to Cyan
    ['#aa00ff', '#ff00aa'], // Purple to Pink
  ];
  const index = name.charCodeAt(0) % gradients.length;
  return `linear-gradient(135deg, ${gradients[index][0]}, ${gradients[index][1]})`;
};

const MindsDirectory = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const data = await getAgents();
      setAgents(data);
    } catch (error) {
      console.error('Failed to load agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentClick = (agent) => {
    setSelectedAgent(selectedAgent?.id === agent.id ? null : agent);
  };

  return (
    <div className="placeholder-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <Button 
          onClick={() => navigate('/')} 
          className="back-button"
          variant="outline"
        >
          <ArrowLeft size={20} /> Back to Home
        </Button>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#00ffff' }}>
          <Brain size={16} />
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{agents.length} AI PERSONALITIES</span>
        </div>
      </div>
      
      <h1 className="page-title">Complete Minds Directory</h1>
      <p className="page-subtitle">All AI personalities with full consciousness profiles</p>
      
      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', marginTop: '3rem' }}>
          <Sparkles className="pulse" style={{ margin: '0 auto', marginBottom: '1rem' }} size={48} />
          <p>Loading consciousness profiles...</p>
        </div>
      ) : (
        <div className="minds-grid">
          {agents.map(agent => (
            <Card 
              key={agent.id} 
              className={`mind-card ${selectedAgent?.id === agent.id ? 'selected' : ''}`}
              onClick={() => handleAgentClick(agent)}
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background:
                  `linear-gradient(rgba(10,10,18,0.86), rgba(10,10,18,0.92)), ` +
                  `url(/sim-backgrounds/${(agent.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}.jpg) center/cover no-repeat`,
                transform: selectedAgent?.id === agent.id ? 'scale(1.05)' : 'scale(1)',
                border: selectedAgent?.id === agent.id ? '2px solid #00ffff' : '1px solid rgba(0,255,255,0.2)'
              }}
            >
              <div className="mind-header">
                <div 
                  className="mind-avatar" 
                  style={{ background: getAvatarGradient(agent.name) }}
                >
                  {agent.name.charAt(0)}
                </div>
                <div className="mind-info">
                  <h3 className="mind-name">{agent.name}</h3>
                  <p className="mind-specialty">{agent.specialty || 'General AI'}</p>
                </div>
              </div>
              <div className="transcendence-section">
                <div className="transcendence-bar">
                  <div 
                    className="transcendence-fill" 
                    style={{ width: `${agent.transcendence_level}%` }}
                  />
                </div>
                <span className="transcendence-label">
                  Transcendence: {agent.transcendence_level.toFixed(1)}%
                </span>
              </div>
              {agent.current_task && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  background: 'rgba(0,0,0,0.3)', 
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: '#b0b0b0'
                }}>
                  <strong style={{ color: '#00ffff' }}>Current Task:</strong><br/>
                  {agent.current_task}
                </div>
              )}
              <Badge className="status-badge-mind" style={{
                marginTop: '1rem',
                background: agent.status === 'online' ? 'rgba(0,255,100,0.2)' : 'rgba(100,100,100,0.2)',
                color: agent.status === 'online' ? '#00ff88' : '#888'
              }}>
                {agent.status}
              </Badge>
            </Card>
          ))}
        </div>
      )}
      
      {selectedAgent && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(180deg, rgba(10,10,15,0.95) 0%, rgba(10,10,15,0.98) 100%)',
          borderTop: '2px solid #00ffff',
          padding: '2rem',
          maxHeight: '40vh',
          overflowY: 'auto',
          animation: 'slideUp 0.3s ease',
          zIndex: 1000,
          boxShadow: '0 -4px 20px rgba(0,255,255,0.3)'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
              <div>
                <h2 style={{ color: '#00ffff', fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                  {selectedAgent.name}
                </h2>
                <p style={{ color: '#888', fontSize: '0.9rem' }}>{selectedAgent.specialty}</p>
              </div>
              <Button 
                variant="ghost" 
                onClick={() => setSelectedAgent(null)}
                style={{ color: '#00ffff' }}
              >
                Close ✕
              </Button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ padding: '1rem', background: 'rgba(0,255,255,0.1)', borderRadius: '8px' }}>
                <div style={{ color: '#00ffff', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Type</div>
                <div style={{ color: '#fff', fontWeight: 'bold' }}>{selectedAgent.type}</div>
              </div>
              
              <div style={{ padding: '1rem', background: 'rgba(0,255,255,0.1)', borderRadius: '8px' }}>
                <div style={{ color: '#00ffff', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Status</div>
                <div style={{ color: selectedAgent.status === 'online' ? '#00ff88' : '#888', fontWeight: 'bold' }}>
                  {selectedAgent.status}
                </div>
              </div>
              
              <div style={{ padding: '1rem', background: 'rgba(0,255,255,0.1)', borderRadius: '8px' }}>
                <div style={{ color: '#00ffff', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Transcendence Level</div>
                <div style={{ color: '#fff', fontWeight: 'bold' }}>{selectedAgent.transcendence_level.toFixed(1)}%</div>
              </div>
              
              <div style={{ padding: '1rem', background: 'rgba(0,255,255,0.1)', borderRadius: '8px' }}>
                <div style={{ color: '#00ffff', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Last Active</div>
                <div style={{ color: '#fff', fontSize: '0.85rem' }}>
                  {selectedAgent.last_heartbeat ? new Date(selectedAgent.last_heartbeat).toLocaleTimeString() : 'Active now'}
                </div>
              </div>
            </div>
            
            {selectedAgent.current_task && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'rgba(0,255,255,0.05)', 
                borderLeft: '3px solid #00ffff',
                borderRadius: '4px'
              }}>
                <div style={{ color: '#00ffff', fontSize: '0.8rem', marginBottom: '0.5rem' }}>CURRENT TASK</div>
                <div style={{ color: '#fff' }}>{selectedAgent.current_task}</div>
              </div>
            )}
            
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <Button 
                onClick={() => navigate(`/agent/${selectedAgent.id}`)}
                style={{ 
                  background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                  color: '#000',
                  fontWeight: 'bold'
                }}
              >
                <Brain size={18} style={{ marginRight: '0.5rem' }} />
                Chat with {selectedAgent.name}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MindsDirectory;
